<template>
  <div class="h-full">
    <BaseTable
      table-id="customer-details-ranges"
      :headers="tableHeaders"
      :list="filteredItems"
      hide-grouping
      :clickable="mode === 'edit'"
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @add-new="onAddRange"
      @table-item-clicked="onSelectRange"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>

    <CustomerDetailsModalRanges
      v-if="isModalAddActive && modifiedRange"
      @close="isModalAddActive = false"
      @save="onSaveRange"
      :exclude-trader-sites="excludedTraderSiteIds"
      :is-new="isNewItem"
      :trader-sites="traderSites"
      :item="modifiedRange"
      :mode="rangeModalMode" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.traderSite?.name || '' }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { deepCopy } from '@/utils/data';
import { filterData, sortData } from '@/utils/table';
import BaseTable from '../common/BaseTable.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import CustomerDetailsModalRanges from './CustomerDetailsModalRanges.vue';

import type { RangeCustomerTradersite } from '@/types/Ranges';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { TraderSite } from '@/types/Traders';

const props = defineProps<{
  items?: RangeCustomerTradersite[];
  mode: 'view' | 'edit';
  customerId?: string;
  traderSiteId?: string;
  traderSites: TraderSite[];
}>();

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
}>({
  query: '',
  column: undefined,
  order: undefined,
});
const isNewItem = ref(false);

const excludedTraderSiteIds = computed<string[]>(() => {
  if (props.items) return props.items.map((x) => x.traderSiteId);
  return [];
});

const filteredItems = computed<RangeCustomerTradersite[]>(() => {
  if (!props.items) return [];
  let list = props.items.filter((x) => x.recordMode !== RecordMode.Deleted);

  if (filterState.query) {
    list = filterData(list, filterState.query, [
      'range.name',
      'range.code',
      'traderSite.name',
      'traderSite.code',
    ]);
  }

  if (filterState.column !== undefined && filterState.order) {
    list = sortData(list, filterState.column, filterState.order, tableHeaders);
  }

  return list;
});

const emit = defineEmits(['add', 'refreshData', 'update', 'delete']);

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  {
    value: 'range.code',
    text: $t('ranges.code'),
    type: 'text',
    active: true,
  },
  {
    value: 'range.name',
    text: $t('ranges.name'),
    type: 'text',
    active: true,
  },
  {
    value: 'hasDynamicRange',
    text: $t('ranges.hasDynamicRange'),
    type: 'check',
    active: true,
  },
  {
    value: 'traderSite.code',
    text: $t('ranges.traderSiteCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSite.name',
    text: $t('ranges.traderSite'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode === 'edit' },
];

const isModalAddActive = ref(false);
const modifiedRange = ref<RangeCustomerTradersite>();
const rangeModalMode = ref<'view' | 'edit'>('view');

function initRange(): RangeCustomerTradersite {
  return {
    id: uuidv4(),
    rangeId: '',
    customerId: props.customerId || '',
    traderSiteId: props.traderSiteId || '',
    hasDynamicRange: false,
    isActive: true,
    isFromManager: true,
    range: {
      id: '',
      code: '',
      name: '',
    },
    traderSite: {
      id: '',
      code: '',
      name: '',
    },
    recordMode: RecordMode.Added,
  };
}

function onAddRange() {
  modifiedRange.value = initRange();
  rangeModalMode.value = 'edit';
  isModalAddActive.value = true;
  isNewItem.value = true;
}

function onSelectRange(range: RangeCustomerTradersite) {
  modifiedRange.value = deepCopy(range);
  rangeModalMode.value = range.isFromManager ? 'edit' : 'view';
  isModalAddActive.value = true;
  isNewItem.value = false;
}

function onSaveRange(range: RangeCustomerTradersite) {
  isModalAddActive.value = false;
  if (isNewItem.value && range.recordMode == RecordMode.Added) emit('add', range);
  else emit('update', range);
  isNewItem.value = false;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<RangeCustomerTradersite>();

function onDeleteRow(item: RangeCustomerTradersite) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  modalDeleteRow.value = false;
  isNewItem.value = false;
  emit('delete', [deleteRowItem.value]);
}

function onDeleteMultiselect(items: RangeCustomerTradersite[]) {
  modalDeleteRow.value = false;
  isNewItem.value = false;
  items.forEach((item) => {
    if (item.isFromManager) {
      emit('delete', [item]);
    }
  });
}

function onRefreshData(options: TableFilterData) {
  filterState.query = options.query ?? '';
  if (options.column !== undefined && options.order) {
    filterState.column = options.column;
    filterState.order = options.order;
  }
}
</script>
